import React, { Component, Fragment } from 'react';
import injectSheet from 'react-jss';
import { Link } from 'react-router-dom-v5-compat';

import { EM_DASH } from '~/common/constants/unicode';
import { Button } from '~/eds/src/components/Button/Button';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { injectIntl } from 'react-intl';
import { withFormatDate } from '~/i18n/hocs/withFormatDate';
import HtmlContent from '~/ui/components/HtmlContent/HtmlContent';

import { documentTypeIcon } from '../../DocumentsPage/Document.utils';
import EcCard from '../../Shared/EcCard/EcCard';
import EcCheckbox from '../../Shared/EcCheckbox/EcCheckbox';
import EcMultipleLocationsLink from '../../Shared/EcMultipleLocationsLink/EcMultipleLocationsLink';
import DownloadResultItemMenu from './DownloadResultItemMenu';
import styles from './SearchResultsItem.styles';

class SearchResultsItem extends Component {
  constructor(props) {
    super(props);

    this.handleOnDetailsClick = this.handleOnDetailsClick.bind(this);
    this.handleOnShowAllPartiesClick =
      this.handleOnShowAllPartiesClick.bind(this);
    this.handleOnSelectClick = this.handleOnSelectClick.bind(this);

    this.state = {
      showDetails: this.props.showExpanded,
      showAllParties: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.showExpanded !== prevProps.showExpanded) {
      this.setState({ showDetails: this.props.showExpanded });
    }
  }

  handleOnDetailsClick() {
    this.setState((prevState) => ({ showDetails: !prevState.showDetails }));
  }

  handleOnShowAllPartiesClick() {
    this.setState({ showAllParties: true });
  }

  handleOnSelectClick() {
    const { document, onSelect } = this.props;

    onSelect(document);
  }

  renderProvisionsTable(provisions) {
    const { classes, intl } = this.props;

    if (!provisions || !provisions.length) {
      return null;
    }

    const sortedProvisions = provisions.sort((a, b) =>
      a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1,
    );

    return (
      <table className={classes.provisionsTable}>
        <thead>
          <tr>
            <th className={classes.provisionsHeaderTitle}>
              {intl.formatMessage({
                id: 'SearchResultsItem.clauseTitle',
                defaultMessage: 'Clause Title',
                description: 'Table header for clause title',
              })}
            </th>
            <th className={classes.provisionsHeaderText}>
              {intl.formatMessage({
                id: 'SearchResultsItem.clauseText',
                defaultMessage: 'Clause Text',
                description: 'Table header for clause text',
              })}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedProvisions.map((provision, index) => (
            <tr key={index}>
              <td className={classes.provisionsBodyTitle}>{provision.name}</td>
              <td className={classes.provisionsBodyText}>
                {provision.content.map((content, provisionIndex) => {
                  return (
                    <p key={provisionIndex}>
                      <HtmlContent html={content} />
                    </p>
                  );
                })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  renderItemProvisions() {
    const { classes, document, intl } = this.props;
    const { showDetails } = this.state;

    if (
      document.provision_list &&
      !document.provision_list.length &&
      document.rebate_table &&
      !document.rebate_table.length &&
      document.payment_table &&
      !document.payment_table.length
    ) {
      return null;
    }

    return (
      <Fragment>
        {showDetails ? (
          <Button
            text={intl.formatMessage({
              id: 'SearchResultsItem.hideDetails',
              defaultMessage: 'Hide Details',
              description: 'Button to hide details',
            })}
            variant="action"
            icon="compress"
            iconPosition="left"
            onClick={this.handleOnDetailsClick}
          />
        ) : (
          <Button
            text={intl.formatMessage({
              id: 'SearchResultsItem.showDetails',
              defaultMessage: 'Show Details',
              description: 'Button to show details',
            })}
            variant="action"
            icon="expand"
            iconPosition="left"
            id={`analyzer-results-show-details-${document.document_handlers[0]?.id}-link`}
            onClick={this.handleOnDetailsClick}
          />
        )}
        {showDetails && (
          <div className={classes.provisionsWrapper}>
            {this.renderProvisionsTable(document.provision_list)}
          </div>
        )}
      </Fragment>
    );
  }

  renderDateItem(title, date) {
    const { classes, formatDate, flags } = this.props;
    const hasLocalization = flags[FlagType.LanguageSelect];
    const formattedDate = formatDate(date, hasLocalization ? 'short' : 'iso');

    return (
      <div className={classes.metadataItem}>
        <div className={classes.metadataHeader}>{title}</div>
        <div className={classes.metadataBody}>{formattedDate}</div>
      </div>
    );
  }

  renderListItem(title, list) {
    const { classes } = this.props;

    return (
      <div className={classes.metadataItem}>
        <div className={classes.metadataHeader}>{title}</div>
        <div className={classes.metadataBody}>
          {list && list.length
            ? list.map((item, index) => (
                <div
                  key={`${title}-${index}`}
                  className={classes.metadataListItem}
                >
                  {item}
                </div>
              ))
            : EM_DASH}
        </div>
      </div>
    );
  }

  renderPartiesListItem(parties) {
    const { classes, intl } = this.props;
    const { showAllParties } = this.state;

    return (
      <div className={classes.metadataItem}>
        <div className={classes.metadataHeader}>
          {intl.formatMessage({
            id: 'SearchResultsItem.partiesHeader',
            defaultMessage: 'PARTIES',
            description: 'Header for parties metadata',
          })}
        </div>
        <div className={classes.metadataBody}>
          {parties && parties.length ? (
            !showAllParties && parties.length > 2 ? (
              <Fragment>
                <div className={classes.metadataListItem}>{parties[0]}</div>
                <div className={classes.metadataListItem}>{parties[1]}</div>
                <button
                  className={classes.metadataListShowAll}
                  onClick={this.handleOnShowAllPartiesClick}
                >
                  {intl.formatMessage({
                    id: 'SearchResultsItem.showAllParties',
                    defaultMessage: 'Show All Parties',
                    description: 'Button to show all parties',
                  })}
                </button>
              </Fragment>
            ) : (
              parties.map((party, index) => (
                <div
                  key={`party-${index}`}
                  className={classes.metadataListItem}
                >
                  {party}
                </div>
              ))
            )
          ) : (
            EM_DASH
          )}
        </div>
      </div>
    );
  }

  renderItemMetadata() {
    const { classes, document, intl } = this.props;

    return (
      <div className={classes.resultsItemMetadata}>
        {this.renderPartiesListItem(document.party_list)}
        {this.renderDateItem(
          intl.formatMessage({
            id: 'SearchResultsItem.uploadDate',
            defaultMessage: 'UPLOAD DATE',
            description: 'Upload date metadata label',
          }),
          document.date_added,
        )}
        <Fragment>
          {this.renderDateItem(
            intl.formatMessage({
              id: 'SearchResultsItem.effectiveDate',
              defaultMessage: 'EFFECTIVE DATE',
              description: 'Effective date metadata label',
            }),
            document.effective_date,
          )}
          {this.renderDateItem(
            intl.formatMessage({
              id: 'SearchResultsItem.expirationDate',
              defaultMessage: 'EXPIRATION DATE',
              description: 'Expiration date metadata label',
            }),
            document.ex_date,
          )}
          {this.renderListItem(
            intl.formatMessage({
              id: 'SearchResultsItem.contractType',
              defaultMessage: 'CONTRACT TYPE',
              description: 'Contract type metadata label',
            }),
            document.contract_type_list,
          )}
        </Fragment>
      </div>
    );
  }

  renderItemHeader() {
    const { classes, document, isSelected, onNavigate, intl } = this.props;
    const hasMultipleLocations = document.document_handlers.length > 1;
    const documentHandler = document.document_handlers[0];

    if (!documentHandler) {
      return (
        <div>
          {intl.formatMessage({
            id: 'SearchResultsItem.documentLoadError',
            defaultMessage: 'An error occurs while loading this document.',
            description: 'Error message for failed document load',
          })}
        </div>
      );
    }

    return (
      <div className={classes.resultsItemHeader}>
        <div className={classes.resultsItemTitle}>
          <EcCheckbox
            id={`doc-id-${documentHandler.id}-checkbox`}
            checked={isSelected}
            onClick={this.handleOnSelectClick}
          />
          <label
            className="screenReaderText"
            htmlFor={`doc-id-${documentHandler.id}-checkbox`}
          >
            {intl.formatMessage(
              {
                id: 'SearchResultsItem.selectDocument',
                defaultMessage: 'select {documentName}',
                description:
                  'Label for selecting a document. Example: documentName = "contract.pdf"',
              },
              { documentName: documentHandler.document_name },
            )}
          </label>
          <div className={classes.resultsItemIcon}>
            {documentTypeIcon(documentHandler.file_type)}
          </div>
          {hasMultipleLocations ? (
            <EcMultipleLocationsLink
              documentName={documentHandler.document_name}
              documentHandlers={document.document_handlers}
              wrapperStyles={classes.resultsItemNameLink}
              multipleLocationsCallback={onNavigate}
            >
              <span className={classes.resultsItemName}>
                {documentHandler.document_name}
              </span>
            </EcMultipleLocationsLink>
          ) : (
            <Link
              to={`/document/${documentHandler.id}`}
              onClick={() => onNavigate(documentHandler)}
            >
              <span className={classes.groupDocumentName}>
                {documentHandler.document_name}
              </span>
            </Link>
          )}
        </div>
        <DownloadResultItemMenu document={document} />
      </div>
    );
  }

  render() {
    const { classes } = this.props;

    return (
      <div className={classes.resultsItemCardWrapper}>
        <EcCard contentStyles={{ padding: '16px' }}>
          {this.renderItemHeader()}
          {this.renderItemMetadata()}
          {this.renderItemProvisions()}
        </EcCard>
      </div>
    );
  }
}

export default injectSheet(styles)(
  withFormatDate(withFlags(injectIntl(SearchResultsItem))),
);
